import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const OwnerModal = ({ isOpen, onClose, owner, onSave }) => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        owner_type: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        address: '',
    });
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (owner) {
            setFormData({
                owner_type: owner.owner_type || '',
                first_name: owner.first_name || '',
                last_name: owner.last_name || '',
                phone_number: owner.phone_number || '',
                email: owner.email || '',
                address: owner.address || '',
            });
        } else {
            setFormData({
                owner_type: '',
                first_name: '',
                last_name: '',
                phone_number: '',
                email: '',
                address: '',
            });
        }
        setErrors([]);
    }, [owner, isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);

        try {
            const url = owner ? `/api/v1/owners/${owner.id}` : '/api/v1/owners';
            const method = owner ? 'PATCH' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: JSON.stringify({ owner: formData }),
            });

            const data = await response.json();

            if (response.ok) {
                onSave(data);
                onClose();
            } else {
                setErrors(data.errors || ['An error occurred']);
            }
        } catch (error) {
            console.error('Error saving owner:', error);
            setErrors(['Failed to save owner. Please try again.']);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    if (!isOpen) return null;

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
        },
        modal: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
        },
        header: {
            padding: '1.5rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: isDark ? '#9ca3af' : '#6b7280',
            padding: '0.5rem',
        },
        body: {
            padding: '1.5rem',
        },
        formGroup: {
            marginBottom: '1.25rem',
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#d1d5db' : '#374151',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            boxSizing: 'border-box',
        },
        select: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            boxSizing: 'border-box',
        },
        textarea: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            minHeight: '100px',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
        },
        errorContainer: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
        },
        errorList: {
            margin: 0,
            paddingLeft: '1.25rem',
        },
        footer: {
            padding: '1.5rem',
            borderTop: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
        },
        button: {
            padding: '0.75rem 1.5rem',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: '0.2s',
        },
        cancelButton: {
            background: isDark ? '#374151' : '#e5e7eb',
            color: isDark ? '#d1d5db' : '#374151',
        },
        saveButton: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h2 style={styles.title}>{owner ? 'Edit Owner' : 'Add New Owner'}</h2>
                    <button onClick={onClose} style={styles.closeButton}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={styles.body}>
                        {errors.length > 0 && (
                            <div style={styles.errorContainer}>
                                <ul style={styles.errorList}>
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Owner Type *</label>
                            <select
                                name="owner_type"
                                value={formData.owner_type}
                                onChange={handleChange}
                                required
                                style={styles.select}
                            >
                                <option value="">Select type...</option>
                                <option value="Individual">Individual</option>
                                <option value="Company">Company</option>
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>First Name *</label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Enter first name"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Last Name *</label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Enter last name"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Phone Number *</label>
                            <input
                                type="tel"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Enter phone number"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                placeholder="Enter email address"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Address</label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                style={styles.textarea}
                                placeholder="Enter address (optional)"
                            />
                        </div>
                    </div>

                    <div style={styles.footer}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...styles.button, ...styles.cancelButton }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{ ...styles.button, ...styles.saveButton }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            {loading ? 'Saving...' : owner ? 'Update Owner' : 'Create Owner'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OwnerModal;
